/**
 * fetchWithTimeout + exponential backoff for transient skin API failures.
 */
import { fetchWithTimeout } from './fetchWithTimeout';
import {
  RETRY,
  computeBackoffMs,
  isAbortError,
  isRetryableError,
  isRetryableHttpStatus,
  isRetryableApiPayload,
  isTimeoutError,
  logSkinResilience,
  sleep,
} from './skinResilience';

function enrichFetchError(err, { timeoutMs, label }) {
  if (isAbortError(err) && !err.reason) {
    const timeoutErr = Object.assign(new Error(`Request timed out after ${timeoutMs}ms`), {
      name: 'AbortError',
      reason: 'timeout',
      timeoutMs,
      label,
    });
    return timeoutErr;
  }
  return err;
}

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { timeoutMs?: number, signal?: AbortSignal }} [init]
 * @param {{ maxAttempts?: number, label?: string, onRetry?: Function, retry?: boolean }} [options]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(input, init = {}, options = {}) {
  const {
    maxAttempts = RETRY.MAX_ATTEMPTS,
    label = 'request',
    onRetry,
    retry = true,
  } = options;

  const { timeoutMs, signal, ...rest } = init;
  let lastError = null;
  let lastData = null;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      logSkinResilience('abort', { label, attempt });
      throw Object.assign(new Error('Request aborted'), { name: 'AbortError' });
    }

    try {
      const response = await fetchWithTimeout(input, { ...rest, timeoutMs, signal });
      let data = null;
      if (!response.ok) {
        try {
          data = await response.clone().json();
        } catch (_) {
          data = null;
        }
      }

      const shouldRetry = retry
        && attempt < maxAttempts - 1
        && (isRetryableHttpStatus(response.status) || isRetryableApiPayload(data));

      if (shouldRetry) {
        const delayMs = computeBackoffMs(attempt);
        logSkinResilience('retry', {
          label,
          attempt: attempt + 1,
          maxAttempts,
          delayMs,
          status: response.status,
          retryable: data?.retryable,
        });
        if (typeof onRetry === 'function') {
          const reconnecting = isRetryableHttpStatus(response.status) || Boolean(data?.retryable);
          if (reconnecting) {
            logSkinResilience('reconnect', { label, attempt: attempt + 1, status: response.status });
          }
          onRetry({ attempt: attempt + 1, maxAttempts, delayMs, status: response.status, retryable: true, reconnecting });
        }
        await sleep(delayMs, signal);
        continue;
      }

      return response;
    } catch (err) {
      const enriched = enrichFetchError(err, { timeoutMs, label });
      lastError = enriched;
      lastData = null;

      if (isAbortError(enriched) && !isTimeoutError(enriched)) {
        logSkinResilience('abort', { label, attempt, message: enriched.message });
        throw enriched;
      }

      const canRetry = retry && attempt < maxAttempts - 1 && isRetryableError(enriched, lastData);
      if (!canRetry) {
        if (isTimeoutError(enriched)) {
          logSkinResilience('timeout', { label, attempt: attempt + 1, timeoutMs });
        } else {
          logSkinResilience('retry_exhausted', { label, attempts: attempt + 1, message: enriched.message });
        }
        throw enriched;
      }

      const delayMs = computeBackoffMs(attempt);
      logSkinResilience('retry', {
        label,
        attempt: attempt + 1,
        maxAttempts,
        delayMs,
        reason: enriched.message,
        timeout: isTimeoutError(enriched),
      });
      if (typeof onRetry === 'function') {
        const reconnecting = isTimeoutError(enriched)
          || isRetryableHttpStatus(enriched.status);
        if (reconnecting) {
          logSkinResilience('reconnect', { label, attempt: attempt + 1 });
        }
        onRetry({
          attempt: attempt + 1,
          maxAttempts,
          delayMs,
          timeout: isTimeoutError(enriched),
          reconnecting,
        });
      }
      await sleep(delayMs, signal);
    }
  }

  throw lastError || new Error('Request failed');
}

/**
 * JSON helper with retry semantics and normalized error objects.
 */
export async function fetchJsonWithRetry(input, init = {}, options = {}) {
  const response = await fetchWithRetry(input, init, options);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || response.statusText || 'Request failed');
    error.status = response.status;
    error.apiResponse = data;
    error.retryable = Boolean(data.retryable) || isRetryableHttpStatus(response.status);
    throw error;
  }
  return data;
}
